/**
 * ReferenceSheetPreview - Shows generated reference sheet for user approval
 *
 * Displays the reference sheet in a preview viewport before finalizing the character.
 */

import React, { useState } from 'react';
import {
  CircleNotchIcon,
  ArrowsClockwiseIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowsOutIcon
} from '@phosphor-icons/react';
import { FullscreenImageViewer } from '../FullscreenImageViewer';

interface ReferenceSheetPreviewProps {
  referenceSheetUrl: string;
  isSaving: boolean;
  characterName: string;
  isRegenerating: boolean;
  onRegenerate: () => void;
  onConfirm: () => void;
  onBack: () => void;
}

export default function ReferenceSheetPreview({
  referenceSheetUrl,
  isSaving,
  characterName,
  isRegenerating,
  onRegenerate,
  onConfirm,
  onBack
}: ReferenceSheetPreviewProps) {
  const [showFullscreen, setShowFullscreen] = useState(false);
  const displayedSheetUrl = referenceSheetUrl;
  const busy = isRegenerating || isSaving;

  return (
    <div className="flex flex-col h-full bg-onyx text-white select-none">
      {/* Header */}
      <div className="px-6 py-4 border-b border-graphite">
        <h2 className="text-lg font-semibold text-white">Reference Sheet Preview</h2>
        <p className="text-sm text-gray-400 mt-1">
          Review the generated reference sheet for {characterName || 'your character'}
        </p>
      </div>

      {/* Main Preview Area */}
      <div className="flex-1 flex items-center justify-center p-8 min-h-0">
        <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
          {isRegenerating ? (
            <div className="flex flex-col items-center gap-4">
              <CircleNotchIcon className="w-12 h-12 text-slate-blue animate-spin" />
              <span className="text-gray-400">Regenerating reference sheet...</span>
            </div>
          ) : (
            <div className="relative group">
              <img
                src={displayedSheetUrl}
                alt="Character Reference Sheet"
                className="w-full h-full object-contain shadow-2xl"
              />
              {/* Fullscreen button overlay */}
              <button
                onClick={() => setShowFullscreen(true)}
                className="absolute top-4 right-4 p-2 bg-black/60 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                title="View fullscreen"
              >
                <ArrowsOutIcon className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="px-6 py-4 border-t border-graphite bg-zinc-900">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-4">
          {/* Back Button */}
          <button
            onClick={onBack}
            disabled={busy}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-gray-400 hover:text-white hover:bg-graphite transition-all disabled:opacity-50"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back to Editor</span>
          </button>

          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* Regenerate Button */}
            <button
              onClick={onRegenerate}
              disabled={busy}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-graphite text-gray-400 hover:text-white hover:bg-iron transition-all disabled:opacity-50"
            >
              {isRegenerating ? (
                <CircleNotchIcon className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowsClockwiseIcon className="w-4 h-4" />
              )}
              <span>Regenerate</span>
            </button>

            {/* Confirm Button */}
            <button
              onClick={onConfirm}
              disabled={busy}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-slate-blue to-[#9795FA] text-white font-medium shadow-lg shadow-slate-blue/30 hover:shadow-slate-blue/50 transition-all disabled:opacity-50 active:scale-95"
            >
              {isSaving ? <CircleNotchIcon className="w-4 h-4 animate-spin" /> : <CheckIcon className="w-4 h-4" />}
              <span>{isSaving ? 'Saving…' : 'Confirm & Finish'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen Viewer */}
      {showFullscreen && (
        <FullscreenImageViewer
          images={[{ url: displayedSheetUrl, label: 'Character Reference Sheet' }]}
          currentIndex={0}
          onClose={() => setShowFullscreen(false)}
          fixed={true}
          respectSidebars={true}
        />
      )}
    </div>
  );
}
